import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, memo } from "react";
import { Group, Layer, Rect, Stage, Text } from "react-konva";
import { useGesture } from "@use-gesture/react";
import { AutoSizer } from "./AutoSizer";
import { BezierLink } from "./BezierLink";
import { computeTreeLayout, getLinkPoints } from "./utils/agentFlowLayout";

const DEFAULT_NODE_WIDTH = 280;
const DEFAULT_NODE_HEIGHT = 154;
const MIN_SCALE = 0.35;
const MAX_SCALE = 1.9;
const WHEEL_PAN_SPEED = 1.8;
const INITIAL_VIEWPORT_PADDING = 48;
const NODE_HORIZONTAL_PADDING = 18;
const NODE_TOP_PADDING = 16;
const NODE_BOTTOM_PADDING = 18;
const NODE_TITLE_FONT_SIZE = 15;
const NODE_TITLE_LINE_HEIGHT = 18;
const NODE_TITLE_MAX_LINES = 4;
const NODE_SUMMARY_FONT_SIZE = 11;
const NODE_SUMMARY_LINE_HEIGHT = 15;
const NODE_SUMMARY_MAX_LINES = 4;
const NODE_META_FONT_SIZE = 11;
const NODE_META_LINE_HEIGHT = 14;
const NODE_META_MAX_LINES = 2;
const CAUSAL_LINK_SOURCE_OFFSET_Y = -10;
const CAUSAL_LINK_TARGET_OFFSET_X = -12;
const CAUSAL_LINK_TARGET_OFFSET_Y = -10;
const CAUSAL_LINK_CONTROL_OFFSET_X = 18;
const CAUSAL_LINK_CONTROL_OFFSET_Y = -18;

function truncateLine(value = "", maxLength = 96) {
  const text = String(value ?? "").trim();
  if (!text || text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, Math.max(0, maxLength - 1)).trim()}…`;
}

function estimateCharactersPerLine(width = 0, fontSize = 12) {
  const safeWidth = Math.max(40, Number(width) || 0);
  const safeFontSize = Math.max(8, Number(fontSize) || 12);
  return Math.max(8, Math.floor(safeWidth / (safeFontSize * 0.58)));
}

function fitTextToLines(value = "", width = 0, fontSize = 12, maxLines = 1) {
  const text = String(value ?? "").trim();
  if (!text) {
    return "";
  }
  const limit = estimateCharactersPerLine(width, fontSize) * Math.max(1, maxLines);
  return truncateLine(text, limit);
}

function estimateWrappedLineCount(value = "", width = 0, fontSize = 12, maxLines = 1) {
  const text = String(value ?? "").trim();
  if (!text) {
    return 0;
  }
  const charsPerLine = estimateCharactersPerLine(width, fontSize);
  const estimatedLines = Math.ceil(text.length / charsPerLine);
  return Math.max(1, Math.min(maxLines, estimatedLines));
}

function estimateBlockHeight(value = "", width = 0, fontSize = 12, lineHeight = 14, maxLines = 1) {
  return estimateWrappedLineCount(value, width, fontSize, maxLines) * lineHeight;
}

function getNodeTextLayout(node = {}) {
  const width = node.width ?? DEFAULT_NODE_WIDTH;
  const contentWidth = width - NODE_HORIZONTAL_PADDING * 2;
  const titleText = fitTextToLines(node.title, contentWidth, NODE_TITLE_FONT_SIZE, NODE_TITLE_MAX_LINES);
  const summaryText = fitTextToLines(node.summary, contentWidth, NODE_SUMMARY_FONT_SIZE, NODE_SUMMARY_MAX_LINES);
  const questionPreviewText = fitTextToLines(
    node.questionPreview ? `Questions: ${node.questionPreview}` : "",
    contentWidth,
    NODE_SUMMARY_FONT_SIZE,
    2,
  );
  const nextStepText = fitTextToLines(
    node.nextStep ? `Next: ${node.nextStep}` : "",
    contentWidth,
    NODE_SUMMARY_FONT_SIZE,
    2,
  );
  const metaText = fitTextToLines(node.meta, contentWidth, NODE_META_FONT_SIZE, NODE_META_MAX_LINES);
  const titleHeight = estimateBlockHeight(
    titleText,
    contentWidth,
    NODE_TITLE_FONT_SIZE,
    NODE_TITLE_LINE_HEIGHT,
    NODE_TITLE_MAX_LINES,
  );
  const summaryHeight = estimateBlockHeight(
    summaryText,
    contentWidth,
    NODE_SUMMARY_FONT_SIZE,
    NODE_SUMMARY_LINE_HEIGHT,
    NODE_SUMMARY_MAX_LINES,
  );
  const questionPreviewHeight = estimateBlockHeight(
    questionPreviewText,
    contentWidth,
    NODE_SUMMARY_FONT_SIZE,
    NODE_SUMMARY_LINE_HEIGHT,
    2,
  );
  const nextStepHeight = estimateBlockHeight(
    nextStepText,
    contentWidth,
    NODE_SUMMARY_FONT_SIZE,
    NODE_SUMMARY_LINE_HEIGHT,
    2,
  );
  const metaHeight = estimateBlockHeight(
    metaText,
    contentWidth,
    NODE_META_FONT_SIZE,
    NODE_META_LINE_HEIGHT,
    NODE_META_MAX_LINES,
  );
  const idY = NODE_TOP_PADDING + 22 + titleHeight + 4;
  let contentY = idY + (node.id ? 16 : 0) + 8;
  const summaryY = summaryText ? contentY : null;
  contentY += summaryText ? summaryHeight + 8 : 0;
  const questionPreviewY = questionPreviewText ? contentY : null;
  contentY += questionPreviewText ? questionPreviewHeight + 6 : 0;
  const nextStepY = nextStepText ? contentY : null;
  contentY += nextStepText ? nextStepHeight + 6 : 0;
  const requiredHeight = Math.max(
    DEFAULT_NODE_HEIGHT,
    contentY + (metaText ? metaHeight + 12 : 0) + NODE_BOTTOM_PADDING,
  );
  const metaY = metaText ? requiredHeight - NODE_BOTTOM_PADDING - metaHeight : null;

  return {
    contentWidth,
    titleText,
    titleHeight,
    summaryText,
    summaryHeight,
    summaryY,
    questionPreviewText,
    questionPreviewHeight,
    questionPreviewY,
    nextStepText,
    nextStepHeight,
    nextStepY,
    metaText,
    metaHeight,
    metaY,
    idY,
    requiredHeight,
  };
}

function getNodeTheme(status = "") {
  switch (String(status ?? "").trim().toLowerCase()) {
    case "claim":
      return {
        bar: "#8b5cf6",
        stroke: "#c4b5fd",
        fillTop: "#faf7ff",
        fillBottom: "#ede9fe",
        badgeFill: "#ede9fe",
        badgeText: "#6d28d9",
      };
    case "running":
      return {
        bar: "#3b82f6",
        stroke: "#93c5fd",
        fillTop: "#f8fbff",
        fillBottom: "#dbeafe",
        badgeFill: "#dbeafe",
        badgeText: "#1d4ed8",
      };
    case "awaiting_review":
      return {
        bar: "#f97316",
        stroke: "#fdba74",
        fillTop: "#fffaf5",
        fillBottom: "#ffedd5",
        badgeFill: "#ffedd5",
        badgeText: "#c2410c",
      };
    case "awaiting_children":
      return {
        bar: "#ea580c",
        stroke: "#fdba74",
        fillTop: "#fffaf5",
        fillBottom: "#fed7aa",
        badgeFill: "#fed7aa",
        badgeText: "#9a3412",
      };
    case "ready":
      return {
        bar: "#f59e0b",
        stroke: "#fcd34d",
        fillTop: "#fffdf7",
        fillBottom: "#fef3c7",
        badgeFill: "#fef3c7",
        badgeText: "#b45309",
      };
    case "ready_to_reason":
      return {
        bar: "#d97706",
        stroke: "#fcd34d",
        fillTop: "#fffdf7",
        fillBottom: "#fde68a",
        badgeFill: "#fde68a",
        badgeText: "#92400e",
      };
    case "deferred":
      return {
        bar: "#64748b",
        stroke: "#cbd5e1",
        fillTop: "#f8fafc",
        fillBottom: "#e2e8f0",
        badgeFill: "#e2e8f0",
        badgeText: "#475569",
      };
    case "completed":
    case "answered":
      return {
        bar: "#10b981",
        stroke: "#86efac",
        fillTop: "#f6fffb",
        fillBottom: "#dcfce7",
        badgeFill: "#dcfce7",
        badgeText: "#047857",
      };
    case "blocked":
      return {
        bar: "#ef4444",
        stroke: "#fca5a5",
        fillTop: "#fff8f8",
        fillBottom: "#fee2e2",
        badgeFill: "#fee2e2",
        badgeText: "#b91c1c",
      };
    default:
      return {
        bar: "#64748b",
        stroke: "#cbd5e1",
        fillTop: "#ffffff",
        fillBottom: "#f8fafc",
        badgeFill: "#f1f5f9",
        badgeText: "#475569",
      };
  }
}

function estimateNodeHeight(node) {
  return getNodeTextLayout(node).requiredHeight;
}

function isFiniteViewport(viewport) {
  return [viewport.scale, viewport.x, viewport.y].every((value) => Number.isFinite(value));
}

// ── AgentFlowNode — pure memoized Konva component, zero hooks, solid fills ──
// No gradients, no shadows, no opacity, no image cache.
// Every zoom/pan frame only updates x/y/scale on Group wrappers — Konva handles
// the rest with minimal draw calls.
const AgentFlowNode = memo(function AgentFlowNode({ node, selected, onSelect, childrenCount, isCollapsed, onToggleCollapse }) {
  const width = node.width ?? DEFAULT_NODE_WIDTH;
  const layout = getNodeTextLayout(node);
  const height = node.height ?? layout.requiredHeight;
  const theme = getNodeTheme(node.lifecycleStatus);
  const hasChildren = (childrenCount ?? 0) > 0;
  const toggleSize = 24;
  const badgeWidth = 94;
  const badgeGap = 8;
  const toggleX = width - toggleSize - 12;
  const toggleY = 10;
  const badgeX = toggleX - badgeWidth - badgeGap;

  const titleLines = layout.titleText.split("\n");
  const idLines = node.id ? fitTextToLines(node.id, layout.contentWidth, 9, 1).split("\n") : [];
  const summaryLines = layout.summaryText ? layout.summaryText.split("\n") : [];
  const questionLines = layout.questionPreviewText ? layout.questionPreviewText.split("\n") : [];
  const nextStepLines = layout.nextStepText ? layout.nextStepText.split("\n") : [];
  const metaLines = layout.metaText ? layout.metaText.split("\n") : [];

  return (
    <Group
      x={node.x}
      y={node.y}
      onClick={() => onSelect?.(node.selectId ?? node.id)}
      onTap={() => onSelect?.(node.selectId ?? node.id)}
      onMouseEnter={(e) => e.currentTarget.getStage()?.container()?.style.setProperty("cursor", "pointer")}
      onMouseLeave={(e) => e.currentTarget.getStage()?.container()?.style.setProperty("cursor", "default")}
    >
      {/* Card background — solid fill from theme, no gradient */}
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        cornerRadius={18}
        fill={selected ? "#eff6ff" : theme.fillTop}
        stroke={selected ? "#0f172a" : theme.stroke}
        strokeWidth={selected ? 1.6 : 1.1}
        listening={false}
      />
      {/* Left accent bar — solid fill, no gradient */}
      <Rect
        x={0}
        y={0}
        width={6}
        height={height}
        fill={theme.bar}
        listening={false}
      />
      {/* Scope label — verticalAlign="bottom" matches canvas fillText baseline */}
      <Text
        x={NODE_HORIZONTAL_PADDING}
        y={NODE_TOP_PADDING + 11}
        text={node.scopeLabel || "Agent"}
        fontSize={11}
        fontStyle="bold"
        fill="#94a3b8"
        verticalAlign="bottom"
        width={layout.contentWidth}
        listening={false}
      />
      {/* Lifecycle badge background */}
      <Rect
        x={badgeX}
        y={12}
        width={badgeWidth}
        height={22}
        cornerRadius={12}
        fill={theme.badgeFill}
        listening={false}
      />
      {/* Lifecycle badge text — centered in badge */}
      <Text
        x={badgeX}
        y={23}
        width={badgeWidth}
        height={22}
        text={node.lifecycleLabel}
        fontSize={10}
        fontStyle="bold"
        fill={theme.badgeText}
        align="center"
        verticalAlign="middle"
        listening={false}
      />
      {/* Title lines */}
      {titleLines.map((line, i) => (
        <Text
          key={`title-${i}`}
          x={NODE_HORIZONTAL_PADDING}
          y={38 + i * NODE_TITLE_LINE_HEIGHT}
          text={line}
          fontSize={NODE_TITLE_FONT_SIZE}
          fontStyle="bold"
          fill="#0f172a"
          verticalAlign="bottom"
          width={layout.contentWidth}
          listening={false}
        />
      ))}
      {/* Node ID lines */}
      {idLines.map((line, i) => (
        <Text
          key={`id-${i}`}
          x={NODE_HORIZONTAL_PADDING}
          y={layout.idY + i * 12}
          text={line}
          fontSize={9}
          fontStyle="bold"
          fill="#94a3b8"
          fontFamily="monospace"
          verticalAlign="bottom"
          width={layout.contentWidth}
          listening={false}
        />
      ))}
      {/* Summary lines */}
      {summaryLines.map((line, i) => (
        <Text
          key={`summary-${i}`}
          x={NODE_HORIZONTAL_PADDING}
          y={layout.summaryY + i * NODE_SUMMARY_LINE_HEIGHT}
          text={line}
          fontSize={NODE_SUMMARY_FONT_SIZE}
          fill="#475569"
          verticalAlign="bottom"
          width={layout.contentWidth}
          listening={false}
        />
      ))}
      {/* Question preview lines */}
      {questionLines.map((line, i) => (
        <Text
          key={`question-${i}`}
          x={NODE_HORIZONTAL_PADDING}
          y={layout.questionPreviewY + i * NODE_SUMMARY_LINE_HEIGHT}
          text={line}
          fontSize={NODE_SUMMARY_FONT_SIZE}
          fill="#64748b"
          verticalAlign="bottom"
          width={layout.contentWidth}
          listening={false}
        />
      ))}
      {/* Next step lines */}
      {nextStepLines.map((line, i) => (
        <Text
          key={`nextstep-${i}`}
          x={NODE_HORIZONTAL_PADDING}
          y={layout.nextStepY + i * NODE_SUMMARY_LINE_HEIGHT}
          text={line}
          fontSize={NODE_SUMMARY_FONT_SIZE}
          fill="#64748b"
          verticalAlign="bottom"
          width={layout.contentWidth}
          listening={false}
        />
      ))}
      {/* Meta lines */}
      {metaLines.map((line, i) => (
        <Text
          key={`meta-${i}`}
          x={NODE_HORIZONTAL_PADDING}
          y={layout.metaY + i * NODE_META_LINE_HEIGHT}
          text={line}
          fontSize={NODE_META_FONT_SIZE}
          fill="#94a3b8"
          verticalAlign="bottom"
          width={layout.contentWidth}
          listening={false}
        />
      ))}
      {/* Interactive toggle button */}
      {hasChildren && (
        <Group
          x={toggleX}
          y={toggleY}
          onClick={(e) => {
            e.cancelBubble = true;
            onToggleCollapse?.(node.id);
          }}
          onTap={(e) => {
            e.cancelBubble = true;
            onToggleCollapse?.(node.id);
          }}
          onMouseEnter={(e) => e.currentTarget.getStage()?.container()?.style.setProperty("cursor", "pointer")}
          onMouseLeave={(e) => e.currentTarget.getStage()?.container()?.style.setProperty("cursor", "default")}
        >
          <Rect
            x={0}
            y={0}
            width={toggleSize}
            height={toggleSize}
            cornerRadius={12}
            fill="rgba(255,255,255,0.95)"
            stroke={isCollapsed ? "#f59e0b" : "#cbd5e1"}
            strokeWidth={1.2}
          />
          <Text
            x={toggleSize / 2}
            y={toggleSize / 2}
            text={isCollapsed ? "+" : "−"}
            fontSize={14}
            fontStyle="bold"
            fill={isCollapsed ? "#d97706" : "#475569"}
            align="center"
            verticalAlign="middle"
            listening={false}
          />
          <Text
            x={toggleSize + 4}
            y={12}
            text={`(${childrenCount})`}
            fontSize={9}
            fontStyle="bold"
            fill="#64748b"
            verticalAlign="bottom"
            listening={false}
          />
        </Group>
      )}
    </Group>
  );
});

function countDescendants(nodeId, childrenById, visited = new Set()) {
  if (visited.has(nodeId)) return 0;
  const next = new Set(visited);
  next.add(nodeId);
  const children = childrenById.get(nodeId) ?? [];
  let total = children.length;
  children.forEach((child) => {
    total += countDescendants(child, childrenById, next);
  });
  return total;
}

function getDescendantIds(nodeId, childrenById, visited = new Set()) {
  if (visited.has(nodeId)) return new Set();
  const next = new Set(visited);
  next.add(nodeId);
  const children = childrenById.get(nodeId) ?? [];
  const all = new Set(children);
  children.forEach((child) => {
    childrenById.get(child)?.forEach((grandchild) => all.add(grandchild));
    getDescendantIds(child, childrenById, next).forEach((id) => all.add(id));
  });
  return all;
}

function CollapseIndicator({ x, y, width, height, label, onClick }) {
  const indicatorW = Math.max(90, label.length * 8 + 32);
  const indicatorH = 32;
  const ix = x + width - indicatorW / 2;
  const iy = y + height / 2 - indicatorH / 2;
  const [hovered, setHovered] = useState(false);
  return (
    <Group
      x={ix}
      y={iy}
      onClick={onClick}
      onTap={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.getStage()?.container()?.style.setProperty("cursor", "pointer");
        setHovered(true);
      }}
      onMouseLeave={(e) => {
        e.currentTarget.getStage()?.container()?.style.setProperty("cursor", "default");
        setHovered(false);
      }}
    >
      <Rect
        width={indicatorW}
        height={indicatorH}
        cornerRadius={16}
        fill={hovered ? "#e2e8f0" : "#f8fafc"}
        stroke={hovered ? "#475569" : "#94a3b8"}
        strokeWidth={hovered ? 1.5 : 1.2}
        listening={false}
      />
      <Text
        x={14}
        y={5}
        width={indicatorW - 28}
        text={label}
        fontSize={11}
        fontStyle="bold"
        fill={hovered ? "#334155" : "#64748b"}
        align="center"
        fontStyle="normal"
        listening={false}
      />
    </Group>
  );
}

export default function AgentFlowDiagram({
  nodes = [],
  edges = [],
  lineageEdges = [],
  reviewGateEdges = [],
  selectedNodeId = null,
  onSelectNode,
  collapsedBranches = new Set(),
  onCollapseChange,
  emptyMessage = "No agent flow is available yet.",
  diagramId = null,
}) {
  const [viewport, setViewport] = useState({ scale: 1, x: 0, y: 0, initialized: false });
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const gestureRef = useRef(null);
  const fitViewportRef = useRef({ scale: 1, x: 0, y: 0 });
  const fitViewportReadyRef = useRef(false);
  const activeViewportRef = useRef({ scale: 1, x: 0, y: 0 });
  const getMinScale = () => Math.min(MIN_SCALE, fitViewportRef.current.scale || MIN_SCALE);
  const getCurrentViewport = () => (viewport.initialized ? activeViewportRef.current : fitViewportRef.current);
  const getPointerInStage = (clientX, clientY) => {
    const element = gestureRef.current ?? stageRef.current?.container?.() ?? containerRef.current;
    if (!element) {
      return null;
    }
    const rect = element.getBoundingClientRect();
    const offsetLeft = element.clientLeft || 0;
    const offsetTop = element.clientTop || 0;
    return {
      x: clientX - rect.left - offsetLeft,
      y: clientY - rect.top - offsetTop,
    };
  };
  const setActiveViewport = (nextViewport) => {
    if (!isFiniteViewport(nextViewport)) {
      console.warn("[AgentFlowDiagram] rejected invalid viewport", {
        nextViewport,
      });
      return;
    }
    activeViewportRef.current = nextViewport;
    setViewport({ ...nextViewport, initialized: true });
  };

  const sizedNodes = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        width: node.width ?? DEFAULT_NODE_WIDTH,
        height: node.height ?? estimateNodeHeight(node),
      })),
    [nodes],
  );

  const nodeLookup = useMemo(
    () =>
      sizedNodes.reduce((accumulator, node) => {
        accumulator[node.id] = node;
        return accumulator;
      }, {}),
    [sizedNodes],
  );

  // ── Collapse / expand logic ──────────────────────────────────────────────
  const [internalCollapsed, setInternalCollapsed] = useState(() => new Set());
  const collapsedSet = collapsedBranches ?? internalCollapsed;
  const setCollapsed = onCollapseChange ?? setInternalCollapsed;

  // ── Collapse state persisted to URL (deferred — causes perf issues) ──────
  // TODO: Re-add URL persistence with a proper debounced, ref-based approach

  const childrenById = useMemo(() => {
    const map = new Map();
    edges.forEach((e) => {
      if (!map.has(e.from)) map.set(e.from, []);
      map.get(e.from).push(e.to);
    });
    lineageEdges.forEach((e) => {
      if (!map.has(e.from)) map.set(e.from, []);
      map.get(e.from).push(e.to);
    });
    reviewGateEdges.forEach((e) => {
      if (!map.has(e.from)) map.set(e.from, []);
      map.get(e.from).push(e.to);
    });
    return map;
  }, [edges, lineageEdges, reviewGateEdges]);

  // Build a set of all node IDs that should be hidden because an ancestor is collapsed
  const hiddenNodeIds = useMemo(() => {
    const hidden = new Set();
    collapsedSet.forEach((collapsedId) => {
      getDescendantIds(collapsedId, childrenById).forEach((id) => hidden.add(id));
    });
    return hidden;
  }, [collapsedSet, childrenById]);

  // Filter nodes to only visible ones
  const visibleNodes = useMemo(
    () => sizedNodes.filter((n) => !hiddenNodeIds.has(n.id)),
    [sizedNodes, hiddenNodeIds],
  );

  // Filter edges to only those between visible nodes
  const visibleEdges = useMemo(
    () => edges.filter((e) => !hiddenNodeIds.has(e.from) && !hiddenNodeIds.has(e.to)),
    [edges, hiddenNodeIds],
  );

  const visibleLineageEdges = useMemo(
    () => lineageEdges.filter((e) => !hiddenNodeIds.has(e.from) && !hiddenNodeIds.has(e.to)),
    [lineageEdges, hiddenNodeIds],
  );

  const visibleReviewGateEdges = useMemo(
    () => reviewGateEdges.filter((e) => !hiddenNodeIds.has(e.from) && !hiddenNodeIds.has(e.to)),
    [reviewGateEdges, hiddenNodeIds],
  );

  // Collapse indicator positions: for each collapsed node, compute where
  // the indicator should appear (at the collapsed node's position)
  const collapseIndicators = useMemo(() => {
    const indicators = [];
    visibleNodes.forEach((node) => {
      if (collapsedSet.has(node.id)) {
        const descCount = countDescendants(node.id, childrenById);
        if (descCount > 0) {
          indicators.push({
            id: node.id,
            x: node.x ?? 0,
            y: node.y ?? 0,
            width: node.width ?? DEFAULT_NODE_WIDTH,
            height: node.height ?? DEFAULT_NODE_HEIGHT,
            label: `${descCount} collapsed`,
          });
        }
      }
    });
    return indicators;
  }, [visibleNodes, collapsedSet, childrenById]);

  // Toggle handler
  const handleToggleCollapse = useCallback(
    (nodeId) => {
      const next = new Set(collapsedSet);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      setCollapsed(next);
    },
    [collapsedSet, setCollapsed],
  );

  const handleNodeSelect = useCallback(
    (nodeId) => {
      const children = childrenById.get(nodeId) ?? [];
      if (children.length > 0) {
        // Toggle collapse on any node that has children
        handleToggleCollapse(nodeId);
      }
      onSelectNode?.(nodeId);
    },
    [childrenById, handleToggleCollapse, onSelectNode],
  );

  const handleCollapseAll = useCallback(() => {
    const allWithChildren = new Set();
    visibleNodes.forEach((node) => {
      if ((childrenById.get(node.id) ?? []).length > 0) {
        allWithChildren.add(node.id);
      }
    });
    setCollapsed(allWithChildren);
  }, [visibleNodes, childrenById, setCollapsed]);

  const handleExpandAll = useCallback(() => {
    setCollapsed(new Set());
  }, [setCollapsed]);

  const positions = useMemo(() => {
    return computeTreeLayout(
      null,
      sizedNodes,
      edges,
      null,
      { lineageEdges, reviewGateEdges },
    );
  }, [edges, lineageEdges, reviewGateEdges, sizedNodes]);

  const layoutBounds = useMemo(() => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    visibleNodes.forEach((node) => {
      const position = positions[node.id] ?? { x: 0, y: 0 };
      minX = Math.min(minX, position.x);
      minY = Math.min(minY, position.y);
      maxX = Math.max(maxX, position.x + (node.width ?? DEFAULT_NODE_WIDTH));
      maxY = Math.max(maxY, position.y + (node.height ?? DEFAULT_NODE_HEIGHT));
    });

    return {
      minX: Number.isFinite(minX) ? minX : 0,
      minY: Number.isFinite(minY) ? minY : 0,
      maxX: Number.isFinite(maxX) ? maxX : DEFAULT_NODE_WIDTH,
      maxY: Number.isFinite(maxY) ? maxY : DEFAULT_NODE_HEIGHT,
    };
  }, [positions, sizedNodes]);

  // Lightweight graph signature — avoids heavy JSON.stringify on large graphs
  const graphNodeCount = visibleNodes.length;
  const graphEdgeCount = visibleEdges.length + visibleLineageEdges.length + visibleReviewGateEdges.length;
  const collapsedKey = useMemo(
    () => Array.from(collapsedSet).sort().join(","),
    [collapsedSet],
  );
  const graphKey = `${graphNodeCount}:${graphEdgeCount}:${collapsedKey}`;

  const prevGraphKeyRef = useRef("");
  useEffect(() => {
    if (prevGraphKeyRef.current !== graphKey) {
      prevGraphKeyRef.current = graphKey;
      fitViewportReadyRef.current = false;
      setViewport({ scale: 1, x: 0, y: 0, initialized: false });
    }
  }, [graphKey]);

  useLayoutEffect(() => {
    if (!viewport.initialized && fitViewportReadyRef.current && isFiniteViewport(fitViewportRef.current)) {
      activeViewportRef.current = fitViewportRef.current;
      setViewport({ ...fitViewportRef.current, initialized: true });
    }
  }, [viewport.initialized, graphKey, layoutBounds]);

  useGesture(
    {
      onDrag: ({ event, delta: [dx, dy], pinching, cancel, touches }) => {
        if (pinching || touches > 1) {
          cancel?.();
          return;
        }
        event?.preventDefault?.();
        const previous = getCurrentViewport();
        setActiveViewport({
          scale: previous.scale,
          x: previous.x + dx,
          y: previous.y + dy,
        });
      },
      onWheel: ({ event, delta: [dx, dy], ctrlKey }) => {
        if (ctrlKey) {
          return;
        }
        event?.preventDefault?.();
        const previous = getCurrentViewport();
        setActiveViewport({
          scale: previous.scale,
          x: previous.x - dx * WHEEL_PAN_SPEED,
          y: previous.y - dy * WHEEL_PAN_SPEED,
        });
      },
      onPinch: ({ event, first, memo, origin: [pointerX, pointerY], offset: [distanceScale], movement: [movementScale] }) => {
        event?.preventDefault?.();
        const pointer = getPointerInStage(pointerX, pointerY);
        if (!pointer) {
          return;
        }
        if (first) {
          const initialViewport = getCurrentViewport();
          memo = [
            initialViewport.x,
            initialViewport.y,
            pointer.x,
            pointer.y,
            initialViewport.scale,
          ];
        }
        const currentViewport = getCurrentViewport();
        const baseX = memo?.[0] ?? currentViewport.x;
        const baseY = memo?.[1] ?? currentViewport.y;
        const anchorX = memo?.[2] ?? pointer.x;
        const anchorY = memo?.[3] ?? pointer.y;
        const baseScale = memo?.[4] ?? currentViewport.scale;
        const nextScale = Math.max(getMinScale(), Math.min(MAX_SCALE, distanceScale));
        const originX = (anchorX - baseX) / baseScale;
        const originY = (anchorY - baseY) / baseScale;
        const nextViewport = {
          scale: nextScale,
          x: anchorX - originX * nextScale,
          y: anchorY - originY * nextScale,
        };
        setActiveViewport(nextViewport);
        return memo;
      },
    },
    {
      target: gestureRef,
      eventOptions: { passive: false },
      drag: {
        threshold: 2,
        filterTaps: true,
      },
      wheel: {
        eventOptions: { passive: false },
      },
      pinch: {
        eventOptions: { passive: false },
        scaleBounds: () => ({ min: getMinScale(), max: MAX_SCALE }),
        from: () => {
          const currentViewport = getCurrentViewport();
          return [currentViewport.scale, currentViewport.scale];
        },
      },
    },
  );

  if (!visibleNodes.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="min-h-0 flex-1 rounded-2xl border border-slate-200 bg-white touch-none flex flex-col"
      style={{ touchAction: "none" }}
    >
      <div className="flex items-center justify-end gap-2 px-4 py-2 border-b border-slate-100">
        <button
          type="button"
          onClick={handleCollapseAll}
          className="px-3 py-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
        >
          Collapse All
        </button>
        <button
          type="button"
          onClick={handleExpandAll}
          className="px-3 py-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
        >
          Expand All
        </button>
      </div>
      <AutoSizer enableResizeObserver={true}>
        {({ width, height }) => {
          const adjustedBounds = {
            minX: Math.min(...visibleNodes.map((node) => positions[node.id]?.x ?? 0)),
            minY: Math.min(...visibleNodes.map((node) => positions[node.id]?.y ?? 0)),
            maxX: Math.max(
              ...visibleNodes.map((node) => (positions[node.id]?.x ?? 0) + (node.width ?? DEFAULT_NODE_WIDTH)),
            ),
            maxY: Math.max(
              ...visibleNodes.map((node) => (positions[node.id]?.y ?? 0) + (node.height ?? DEFAULT_NODE_HEIGHT)),
            ),
          };
          const contentWidth = Math.max(DEFAULT_NODE_WIDTH, adjustedBounds.maxX - adjustedBounds.minX);
          const contentHeight = Math.max(DEFAULT_NODE_HEIGHT, adjustedBounds.maxY - adjustedBounds.minY);
          const availableWidth = Math.max(1, width - INITIAL_VIEWPORT_PADDING * 2);
          const availableHeight = Math.max(1, height - INITIAL_VIEWPORT_PADDING * 2);
          const fitScale = Math.min(1, availableWidth / contentWidth, availableHeight / contentHeight);
          const fitX = ((width - contentWidth * fitScale) / 2) - adjustedBounds.minX * fitScale;
          const fitY = ((height - contentHeight * fitScale) / 2) - adjustedBounds.minY * fitScale;
          fitViewportRef.current = { scale: fitScale, x: fitX, y: fitY };
          fitViewportReadyRef.current = true;
          const activeViewport = viewport.initialized
            ? { scale: viewport.scale, x: viewport.x, y: viewport.y }
            : fitViewportRef.current;
          if (!isFiniteViewport(activeViewport)) {
            console.warn("[AgentFlowDiagram] render received invalid viewport", {
              viewport,
              fitViewport: fitViewportRef.current,
              activeViewport,
            });
          }
          activeViewportRef.current = activeViewport;

          return (
            <div ref={gestureRef} className="h-full w-full touch-none" style={{ touchAction: "none" }}>
              <Stage
                ref={stageRef}
                width={width}
                height={height}
                x={activeViewport.x}
                y={activeViewport.y}
                scaleX={activeViewport.scale}
                scaleY={activeViewport.scale}
              >
                <Layer>
                  {visibleEdges.map((edge) => (
                    <BezierLink
                      key={`${edge.from}:${edge.to}`}
                      points={getLinkPoints(edge.from, edge.to, positions, nodeLookup)}
                      stroke="#94a3b8"
                      strokeWidth={1.3}
                    />
                  ))}
                  {visibleLineageEdges.map((edge) => (
                    <BezierLink
                      key={`lineage:${edge.from}:${edge.to}`}
                      points={getLinkPoints(edge.from, edge.to, positions, nodeLookup, {
                        sourceOffsetY: CAUSAL_LINK_SOURCE_OFFSET_Y,
                        targetOffsetX: CAUSAL_LINK_TARGET_OFFSET_X,
                        targetOffsetY: CAUSAL_LINK_TARGET_OFFSET_Y,
                        controlOffsetX: CAUSAL_LINK_CONTROL_OFFSET_X,
                        controlOffsetY: CAUSAL_LINK_CONTROL_OFFSET_Y,
                      })}
                      stroke="#6366f1"
                      strokeWidth={1.4}
                      dash={[6, 6]}
                      opacity={0.72}
                      showArrow={false}
                    />
                  ))}
                  {visibleReviewGateEdges.map((edge) => (
                    <BezierLink
                      key={`review-gate:${edge.from}:${edge.to}`}
                      points={getLinkPoints(edge.from, edge.to, positions, nodeLookup, {
                        sourceOffsetY: CAUSAL_LINK_SOURCE_OFFSET_Y,
                        targetOffsetX: CAUSAL_LINK_TARGET_OFFSET_X,
                        targetOffsetY: CAUSAL_LINK_TARGET_OFFSET_Y,
                        controlOffsetX: CAUSAL_LINK_CONTROL_OFFSET_X,
                        controlOffsetY: CAUSAL_LINK_CONTROL_OFFSET_Y,
                      })}
                      stroke="#d97706"
                      strokeWidth={1.8}
                      dash={[8, 6]}
                      opacity={0.9}
                      showArrow={false}
                    />
                  ))}
                  {visibleNodes.map((node) => (
                    <AgentFlowNode
                      key={node.id}
                      node={{
                        ...node,
                        x: positions[node.id]?.x ?? 0,
                        y: positions[node.id]?.y ?? 0,
                      }}
                      selected={selectedNodeId === node.id}
                      onSelect={handleNodeSelect}
                      childrenCount={childrenById.get(node.id)?.length ?? 0}
                      isCollapsed={collapsedSet.has(node.id)}
                      onToggleCollapse={handleToggleCollapse}
                    />
                  ))}
                  {collapseIndicators.map((indicator) => (
                    <CollapseIndicator
                      key={`collapse:${indicator.id}`}
                      x={indicator.x}
                      y={indicator.y}
                      width={indicator.width}
                      height={indicator.height}
                      label={indicator.label}
                      onClick={() => handleToggleCollapse(indicator.id)}
                    />
                  ))}
                </Layer>
              </Stage>
            </div>
          );
        }}
      </AutoSizer>
    </div>
  );
}
